import React, { Fragment, useState } from "react";
import company from "../../../icons/company.svg";
import date from "../../../icons/date.svg";
import { StudentApplicationToApprove } from "../types";
import { useTranslation } from "react-i18next";
import { Table, Segment, Button, Confirm } from "semantic-ui-react";
import GenderImage from "../../../components/GenderImage";
import { formatGradeWithSuffix } from "../../../app/helpers/gradeHelpers";
import StudentPendingApplicationModal from "./StudentPendingApplicationModal";
import ReturnForEditModal from "./ReturnForEditModal";

interface IProps {
  pendingApplications: StudentApplicationToApprove[];
  handleApproveApplication: (studentId: number, applicationId: number) => void;
  handleCancelApplication: (applicationId: number) => void;
  handleReturnedApplication: (applicationId: number) => void;
}

const PendingApplications: React.FC<IProps> = ({
  pendingApplications,
  handleApproveApplication,
  handleCancelApplication,
  handleReturnedApplication,
}) => {
  const { t } = useTranslation();
  const [rejectingApplicationId, setRejectingApplicationId] = useState<number | null>(null);
  const [returningApplicationId, setReturningApplicationId] = useState<number | null>(null);

  return (
    <Fragment>
      <Segment placeholder padded className="pending-registrations-segment">
        <div className="pending-registrations">
          <p>{t("pending_applications_list.description")}</p>
          <div className="pending-registrations-text">
            {t("pending_applications_list.title")}: {pendingApplications.length}
          </div>
          <Table basic="very" className="student-list pending">
            <Table.Body>
              {pendingApplications.map((student: StudentApplicationToApprove) => (
                <Table.Row key={student.applicationId}>
                  <Table.Cell singleLine>
                    <GenderImage gender={student.gender} />
                    {student.studentFullName}, {formatGradeWithSuffix(student.grade, student.isStudent)}
                  </Table.Cell>
                  <Table.Cell>{student.positionTitle}</Table.Cell>
                  <Table.Cell>
                    <span>
                      <img src={company} alt="company" />
                      {student.positionCompany}
                    </span>
                  </Table.Cell>
                  <Table.Cell singleLine>
                    <span>
                      <img src={date} alt="date" />
                      {student.positionDate}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="student-list-actions" singleLine>
                    <StudentPendingApplicationModal
                      studentId={student.id}
                      applicationId={student.applicationId}
                      studentFullName={student.studentFullName}
                      grade={student.isStudent ? "Studentas" : (student.grade ?? "").toString()}
                      handleCancel={handleCancelApplication}
                      handleReturn={handleReturnedApplication}
                    />
                    <Button
                      size="tiny"
                      primary
                      content={t("pending_applications_list.actions.approve")}
                      onClick={() => handleApproveApplication(student.id, student.applicationId)}
                    />
                    <Button
                      size="tiny"
                      negative
                      content={t("common.actions.reject")}
                      onClick={() => setRejectingApplicationId(student.applicationId)}
                    />
                    <Button
                      size="tiny"
                      color="orange"
                      content={t("applications.school.actions.return_for_edit")}
                      onClick={() => setReturningApplicationId(student.applicationId)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Segment>
      <ReturnForEditModal
        applicationId={returningApplicationId}
        onClose={() => setReturningApplicationId(null)}
        onReturned={(applicationId) => {
          setReturningApplicationId(null);
          handleReturnedApplication(applicationId);
        }}
      />
      <Confirm
        open={rejectingApplicationId !== null}
        size="tiny"
        content={t("pending_applications_list.actions.reject_confirm")}
        cancelButton={t("common.actions.cancel")}
        confirmButton={t("common.actions.reject")}
        onCancel={() => setRejectingApplicationId(null)}
        onConfirm={() => {
          if (rejectingApplicationId !== null) {
            handleCancelApplication(rejectingApplicationId);
            setRejectingApplicationId(null);
          }
        }}
      />
    </Fragment>
  );
};

export default PendingApplications;
